import AutoLoadMoreIndicator from '@/components/AutoLoadMoreIndicator'
import { ImageGrid } from '@/components/ImageGridSection'
import LightBox from '@/components/LightBox/LightBox'
import Page from '@/components/Page'
import Picture from '@/components/Picture'
import { GalleryPicture, getListForGalleryInfiniteQueryOptions } from '@/utils/fetchers/post'
import usePostNavigator from '@/utils/hooks/usePostNavigator'
import { Button, SimpleGrid, Stack, Text } from '@mantine/core'
import { useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { groupBy, keyBy } from 'lodash-es'
import { MouseEvent, startTransition, useMemo, useRef, useState } from 'react'
import { useTitle } from 'react-use'
import type { ControllerRef, SlideImage } from 'yet-another-react-lightbox'
import UserPageTabLoading from './-components/UserPageTabLoading'
import styles from './gallery.module.css'

interface GallerySlideImage extends SlideImage {
	galleryPicture: GalleryPicture
}

export const Route = createFileRoute('/u/$username/gallery')({
	component: GalleryPage,
	pendingComponent: UserPageTabLoading,
})

function GalleryPage() {
	const { username } = Route.useParams()
	const { data, fetchNextPage, refetch, isFetchingNextPage } = useSuspenseInfiniteQuery(
		getListForGalleryInfiniteQueryOptions(username!)
	)
	const lightboxControllerRef = useRef<ControllerRef | null>(null)
	const [open, setOpen] = useState(false)
	const [index, setIndex] = useState(0)

	const galleryPageDataList = useMemo(() => data.pages.flatMap((page) => page.data), [data.pages])
	const pictures = galleryPageDataList.flatMap((galleryPage) => galleryPage.pictures)
	const monthGroupedPictures = groupBy(pictures, (picture) =>
		dayjs(picture.createdAt).startOf('month').toISOString()
	)
	const POST_MAP = keyBy(
		galleryPageDataList.flatMap((galleryPage) => galleryPage.posts),
		'id'
	)

	const slides = pictures.map((item) => {
		const picture = item.pictureInfo
		return {
			src: picture.picUrl,
			width: picture.width,
			height: picture.height,
			galleryPicture: item,
		}
	})

	const navigateToPost = usePostNavigator()

	const handleItemClick = (index: number, e: MouseEvent) => {
		const picture = pictures[index]
		const post = POST_MAP[picture?.postId]

		if (!picture || !post) {
			return
		}
		startTransition(() => {
			navigateToPost(post, e)
		})
		lightboxControllerRef.current?.close()
	}
	useTitle(`相册 - 即刻`)

	return (
		<Page>
			<Stack gap={16} className={styles.box}>
				{Object.entries(monthGroupedPictures).map(([month, groupedPictures]) => {
					const isSameYear = dayjs(month).year() === dayjs().year()
					return (
						<SimpleGrid className={styles.section} key={month}>
							<Stack gap={4} justify="flex-start">
								{!isSameYear && (
									<Text unstyled className={styles.sectionYear} span>
										{dayjs(month).format('YYYY年')}
									</Text>
								)}
								<Text unstyled className={styles.sectionTitle} span>
									{dayjs(month).format('M月')}
								</Text>
							</Stack>
							<ImageGrid
								data={groupedPictures.map((item) => item.pictureInfo)}
								mode="compact-sm"
								onItemClick={(item, indexInGroupedPictures) => {
									const index = pictures.findIndex(
										(picture) =>
											picture.pictureInfo.key === item.key &&
											picture.createdAt === groupedPictures[indexInGroupedPictures].createdAt
									)
									setIndex(index)
									setOpen(true)
								}}
							/>
						</SimpleGrid>
					)
				})}
			</Stack>
			<AutoLoadMoreIndicator
				isLoading={isFetchingNextPage}
				onLoadMore={async () => void (await fetchNextPage())}
			/>

			<LightBox
				open={open}
				close={() => setOpen(false)}
				index={index}
				slides={slides}
				controller={{ ref: lightboxControllerRef }}
				className={styles.lightbox}
				toolbar={{
					buttons: [
						<Button
							key="view-post"
							variant="glass"
							onClick={(e: MouseEvent) => {
								const currentIndex =
									lightboxControllerRef.current?.getLightboxState().currentIndex ?? 0
								handleItemClick(currentIndex, e)
							}}
						>
							查看原动态
						</Button>,
						'close',
					],
				}}
				render={{
					slide: ({ slide, offset }) => {
						const picture = (slide as GallerySlideImage).galleryPicture
						if (picture.pictureInfo.livePhoto) {
							return (
								<Picture
									autoPlayOnce={offset === 0}
									isLivePhoto={!!picture.pictureInfo.livePhoto}
									data={picture.pictureInfo.livePhoto}
									src={picture.pictureInfo.picUrl}
									width={picture.pictureInfo.width}
									height={picture.pictureInfo.height}
									className={styles.pictureSlide}
								/>
							)
						}
						return
					},
				}}
			/>
		</Page>
	)
}
